<template lang="html">
  <v-app class="ma-3 white details-container">
    <div v-if="loading" class="loader-container">
      <loader></loader>
    </div>
    <div v-else>
      <v-carousel hide-controls class="task-image__carousal">
         <v-carousel-item
           @click="showGallery(index)"
           v-for="(image, index) in taskMedia"
           :key="index"
           :src="createThumbnailUrl(image.data_url)"
         >
         <a :href="image.data_url" download target="_blank">
           <v-btn icon class="task-image__carousal-button" @click.stop>
             <img src="~icons/download.svg"></img>
           </v-btn>
         </a>
        </v-carousel-item>
       </v-carousel>

      <div class="task-summary mb-2">
        <span class="summary__item" v-if="timeToEvent">
          <v-tooltip top>
            <v-icon slot="activator">access_time</v-icon>
            <span>Time to event</span>
          </v-tooltip>
          <span>{{ timeToEvent }}</span>
        </span>

        <span class="summary__item" v-if="taskLength">
          <v-tooltip top>
            <v-icon slot="activator">timer</v-icon>
            <span>Effort duration</span>
          </v-tooltip>
          <span>{{ taskLength }}</span>
        </span>

        <span class="summary__item" v-if="task.task_virtual == 1">
          <v-tooltip top>
            <v-icon slot="activator">settings_remote</v-icon>
            <span>Virtual task</span>
          </v-tooltip>
          <span>Virtual</span>
        </span>

        <span class="summary__item" v-else>
          <v-tooltip top>
            <v-icon slot="activator">explore</v-icon>
            <span>Physical task</span>
          </v-tooltip>
          <span>Physical</span>
        </span>

        <span class="summary__item" v-if="task.task_location_restricted == 1">
          <v-tooltip top>
            <v-icon slot="activator">location_on</v-icon>
            <span>Location restricted</span>
          </v-tooltip>
          <span>Location {{ locationRadius && `${locationRadius} km`}}
            <a :href="locationURL" target="_blank" class="link">view</a>
          </span>
        </span>
      </div>

      <div class="task-name">
        {{ task.task_name }}
      </div>

      <div class="task-description">
        <div v-if="task.task_description"><vue-markdown>{{ task.task_description }}</vue-markdown></div>
      </div>

      <div class="task-organizer-description" v-if="isOrganizer && task.task_organizer_description">
        <h4>Organizer Info</h4>
        <div v-if="task.task_organizer_description"><vue-markdown>{{ task.task_organizer_description }}</vue-markdown></div>
      </div>

      <div class="task-organizer-description" v-if="isRegistered && task.task_registered_description">
        <h4>Registered User Info</h4>
        <div v-if="task.task_registered_description"><vue-markdown>{{ task.task_registered_description }}</vue-markdown></div>
      </div>

      <div class="task-image__list">
        <span @click="showGallery(index)" class="task-image__item" v-for="(image, index) in taskMedia" :key="image.data_object_id">
          <imager :src="createThumbnailUrl(image.data_url)" :styles="{'min-width': '124px', 'max-height': '124px'}">
            <loader></loader>
          </imager>
          <a :href="image.data_url" download target="_blank">
            <v-btn icon class="task-image__item-button" @click.stop>
              <img src="~icons/download.svg"></img>
            </v-btn>
          </a>
        </span>
      </div>

      <div>
        <v-btn flat small class="link-button" v-if="!showExtras" @click="showExtras=true">
          <span>Show Additional Information</span>
        </v-btn>
        <v-btn flat small class="link-button" v-if="showExtras" @click="showExtras=false">
          <span>Hide Additional Information</span>
        </v-btn>
      </div>

      <div v-if="showExtras">
        <h5 class="extra-label">
          <v-icon small>group_work</v-icon>
          Skills
        </h5>
        <div class="skills">
          <span class="skill__item" v-for="skill in skills">{{ skill.skill_name }}</span>
        </div>

        <div class="details">
          <h5 class="extra-label">
            <v-icon small>flag</v-icon>
            Project
          </h5>
          <div class="details__row" v-if="project.project_id">
            <router-link class="details__button" :to="{ name: 'ProjectOverview', params: {id: project.project_id} }">
              {{ project.project_name }}
            </router-link>
          </div>

          <h5 class="extra-label">
            <v-icon small>person</v-icon>
            Organizer
          </h5>
          <div class="details__row" v-if="taskOrganizer.user_id">
            <router-link class="details__button" :to="{ name: 'UserProfile', params: {id: taskOrganizer.user_id} }">
              @{{ taskOrganizer.username }}
            </router-link>
          </div>


          <h5 class="extra-label">
            <v-icon small>check_circle</v-icon>
            Completion Type
          </h5>
          <div class="details__row">
            {{ task.completion_type === 'multi' ? 'Multiple Completion' : 'Single Completion' }}
          </div>

        </div>
      </div>
    </div>
  </v-app>
</template>

<script>
import moment from 'moment';
import get from 'lodash/get';
import VueMarkdown from 'vue-markdown';
import Imager from '@/components/Imager';
import Loader from '@/components/Loader';
import Question from '@/components/Question';
import { identifyFileType, FILE_TYPES } from '@/utils/file_type';

import audioThumbnail from 'icons/audio-thumbnail.png';
import videoThumbnail from 'icons/video-thumbnail.png';
import documentThumbnail from 'icons/document-thumbnail.png';

export default {
  components: {
    Imager,
    Loader,
    Question,
    VueMarkdown,
  },
  data() {
    return {
      showExtras: false,
    };
  },
  methods: {
    showGallery(index) {
      this.$store.dispatch('gallery/showGallery', { show: true, active: index });
    },
    createThumbnailUrl(url) {
      /* eslint global-require: "warn" */
      const fileType = identifyFileType(url);
      switch (fileType) {
        case FILE_TYPES.IMAGE:
          return url;
        case FILE_TYPES.VIDEO:
          return videoThumbnail;
        case FILE_TYPES.AUDIO:
          return audioThumbnail;
        default:
          return documentThumbnail;
      }
    },
    setActiveChannel() {
      if (this.allowedChannelAccess.length === 0) {
        this.activeChannel = null;
      } else if (this.allowedChannelAccess.includes('organizers')) {
        this.activeChannel = 'organizers';
      } else if (this.allowedChannelAccess.includes('contributors')) {
        this.activeChannel = 'contributors';
      } else if (this.allowedChannelAccess.includes('prospects')) {
        this.activeChannel = 'prospects';
      }
    },
  },
  computed: {
    loading() {
      return this.$store.getters['shared/getDataLoading'];
    },
    timeToEvent() {
      const date = get(this.tasksDates, '[0].task_date');
      if (!date) {
        return null;
      }

      const duration = moment(date);
      return duration.fromNow();
    },
    taskLength() {
      if (!this.task.task_length) return null;

      const minutes = parseInt(this.task.task_length, 10);
      return moment().add(minutes, 'minutes').fromNow(true);
    },
    locationURL() {
      const address = this.$store.getters['tasks/taskAddress'];
      if (!address) return '#';

      return `https://maps.google.com/?q=${address}`;
    },
    locationRadius() {
      return this.$store.getters['tasks/taskRadius'];
    },
    organizers() {
      return this.$store.getters['tasks/organizers'];
    },
    tasksDates() {
      return this.$store.getters['tasks/tasksDates'];
    },
    skills() {
      return this.$store.getters['tasks/skills'];
    },
    taskOrganizer() {
      return get(this.organizers, '[0]', {});
    },
    project() {
      return get(this.task, 'project.project_data', {});
    },
    taskId() {
      return this.$route.params.id;
    },
    taskMedia: {
      get() {
        const images = this.$store.getters['tasks/taskObjects'];
        const imageUrls = [];
        for (let i = 0; i < images.length; i += 1) {
          imageUrls.push(images[i].data_url);
        }
        this.$store.dispatch('gallery/setImages', imageUrls);
        return images;
      },
    },
    task() {
      return this.$store.getters['tasks/task'];
    },
    userId() {
      return this.$store.getters['auth/userId'];
    },
    userTaskScope() {
      return this.$store.getters['tasks/userTaskScope'];
    },
    isOrganizer() {
      return this.userTaskScope.includes('organizer');
    },
    isRegistered() {
      return this.userTaskScope.includes('registered');
    },
  },
  mounted() {
    const taskId = this.taskId;
    this.$store.dispatch('tasks/resetTasksState');
    this.$store.dispatch('tasks/getTaskDetails', taskId);
  },
};
</script>

<style lang="scss" scoped>
@import "~styles/shared.scss";
#app {
    #bottom-fixed {
      z-index: 40;
    }

    .link-button {
      margin-top: auto;
      margin-left: -7px;
      margin-top: 12px;
      margin-bottom: 16px;
      span {
        font-size: 13px;
        font-family: MuseoSans-500;
        color: $blue;
      }
    }

    .task-description {
      padding: inherit;
      word-wrap: break-word;
    }

    .task-image__item {
      position: relative;
      display:inline-block;
      padding:1px;
    }

    .task-image__item .task-image__item-button {
      background: $white;
      position: absolute;
      right: 0;
      bottom: 0;
      margin-right: 10px;
      margin-bottom:
       10px;
    }
    .task-image__carousal .task-image__carousal-button {
      background: $white;
      position: absolute;
      right: 12px;
      top: 12px;
      margin-right: 10px;
      margin-bottom: 10px;
    }
}

.task-summary {
  padding-bottom: 10px;
  border-bottom: 1px solid $lighter;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.summary__item {
  display: inline-block;
  margin-right: 15px;
  margin-top: 10px;
}

.skills {
  margin-bottom: 10px;
  padding-bottom: 10px;
  margin-top: 10px;
  border-bottom: 1px solid $lighter;
}

.skill__item {
  color: $light;
  border-radius: 24px;
  border: solid 1px $light;
  padding: 5px 12px;
  margin-right: 8px;
}

.details {
  // font-size: 16px;
  // color: $light;
}

.details__row {
  margin-bottom: 8px;
  color: $light;
  font-size: 14px !important;
  border-bottom: 1px solid $lighter;
}

.details__row:last-child {
  border-bottom: none;
}

.details__button {
  margin-top: -3px;
  color: $blue;
  text-decoration: none;
}

.loader-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.link {
  text-decoration: none;
  color: $blue;
}

.details-container {
  padding-bottom: 75px;
}

.task-image__carousal {
  position: relative;
  width: 100vw;
  height: 40vh;
  margin-top: -16px;
  margin-left: -16px;
}

@media screen and (orientation: landscape) {
  .task-image__list {
    display: block;
    margin-top: 15px;
  }

  .task-image__carousal {
    display: none;
  }
}

@media screen and (orientation: portrait) {
  .task-image__list {
    display: none;
  }

  .task-image__carousal {
    display: block;
  }
}

.extra-label {
  margin-bottom: 5px;
  // margin-left: 5px;
}

.task-name {
  font-size: 24px;
  font-weight: bold;
  font-family: 'MuseoSans-500';
  line-height: 1.2;
  margin-top: 10px;
  text-decoration: none;
  color: initial;
  margin-bottom: 10px;
}
</style>



// WEBPACK FOOTER //
// src/components/TaskDetails.vue